import type { ReactNode } from 'react'
import { Button, ContentGroup, VerificationCodeInput } from '@/shared/ui'
import { ArrowLeftIcon } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { cn } from '@/shared/lib'

export type AuthPanelCodeVariant = 'desktop' | 'mobile'

export type AuthPanelCodeProps = {
  variant?: AuthPanelCodeVariant
  title?: string
  subtitle?: ReactNode
  resendText?: string
  supportText?: ReactNode
  codeValue: string
  onCodeChange: (value: string) => void
  onSubmit?: () => void
  onBack?: () => void
  submitLabel?: string
  className?: string
}

const defaultSubtitle = (
  <>
    <span>Мы отправили его на почту sveta@epicgrowth.io.</span>
    <br />
    <span>Или перейдите по ссылке в письме.</span>
  </>
)

const defaultSupportText = (
  <>
    <span style={{ fontWeight: designTokens.typography.body.baseSemibold.weight }}>
      Не получается войти?
    </span>
    <br />
    <span>Проверьте спам и написание почты или напишите нам в поддержку.</span>
  </>
)

export function AuthPanelCode({
  variant = 'desktop',
  title = 'Введите код верификации',
  subtitle = defaultSubtitle,
  resendText = 'Запросить код повторно можно через 01:45',
  supportText = defaultSupportText,
  codeValue,
  onCodeChange,
  onSubmit,
  onBack,
  submitLabel = 'Продолжить',
  className,
}: AuthPanelCodeProps) {
  const isMobile = variant === 'mobile'
  const minHeight = isMobile ? designTokens.sizes.minHeight.authPanelCodeMobile : undefined

  const titleStyle = {
    fontFamily: designTokens.typography.fontFamilies.primary,
    fontSize: designTokens.typography.heading.h2.size,
    lineHeight: designTokens.typography.heading.h2.lineHeight,
    fontWeight: designTokens.typography.heading.h2.weight,
    color: designTokens.colors.text.primary,
    textAlign: 'center' as const,
    width: '100%',
  }

  const subtitleStyle = {
    fontFamily: designTokens.typography.fontFamilies.primary,
    fontSize: designTokens.typography.body.md.size,
    lineHeight: designTokens.typography.body.md.lineHeight,
    fontWeight: designTokens.typography.body.md.weight,
    color: designTokens.colors.text.primary,
    textAlign: 'center' as const,
    width: '100%',
  }

  const resendStyle = {
    fontFamily: designTokens.typography.fontFamilies.primary,
    fontSize: designTokens.typography.caption.size,
    lineHeight: designTokens.typography.caption.lineHeight,
    fontWeight: designTokens.typography.caption.weight,
    color: designTokens.colors.text.additional,
    textAlign: 'center' as const,
  }

  const supportStyle = {
    fontFamily: designTokens.typography.fontFamilies.primary,
    fontSize: designTokens.typography.body.base.size,
    lineHeight: designTokens.typography.body.base.lineHeight,
    fontWeight: designTokens.typography.body.base.weight,
    color: designTokens.colors.text.primary,
    textAlign: 'center' as const,
    opacity: 0.9,
  }

  const desktopBack = (
    <button
      type="button"
      className="inline-flex items-center"
      onClick={onBack}
      style={{ gap: designTokens.spacing.authPanelBackGap }}
    >
      <ArrowLeftIcon
        className="h-5 w-5"
        aria-hidden="true"
        style={{ color: designTokens.colors.text.primary }}
      />
      <span
        style={{
          fontFamily: designTokens.typography.fontFamilies.primary,
          fontSize: designTokens.typography.body.base.size,
          lineHeight: 'normal',
          fontWeight: designTokens.typography.body.base.weight,
          color: designTokens.colors.text.primary,
        }}
      >
        Назад
      </span>
    </button>
  )

  const mobileBack = (
    <Button type="button" variant="tertiaryCancelIcon" onClick={onBack} aria-label="Back">
      <ArrowLeftIcon className="h-8 w-8" aria-hidden="true" />
    </Button>
  )

  const codeBlock = (
    <div className="flex w-full flex-col items-center" style={{ gap: designTokens.spacing.md }}>
      <VerificationCodeInput value={codeValue} onChange={onCodeChange} />
      <div style={resendStyle}>{resendText}</div>
    </div>
  )

  const button = (
    <Button type="submit" variant={isMobile ? 'primaryMobile' : 'primary'} className="w-full">
      {submitLabel}
    </Button>
  )

  if (isMobile) {
    return (
      <div className={cn('w-full', className)} style={{ minHeight }}>
        <form
          className="w-full"
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit?.()
          }}
          noValidate
        >
          <ContentGroup
            variant="registrationMobile"
            className="h-full w-full"
            style={minHeight ? { minHeight } : undefined}
          >
            <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxxl }}>
              <div className="flex w-full items-center" style={{ gap: designTokens.spacing.xxxl }}>
                {mobileBack}
                <h2 style={titleStyle}>{title}</h2>
              </div>
              <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxl }}>
                <div style={subtitleStyle}>{subtitle}</div>
                {codeBlock}
              </div>
            </div>
            <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
              <div style={supportStyle}>{supportText}</div>
              {button}
            </div>
          </ContentGroup>
        </form>
      </div>
    )
  }

  return (
    <form
      className={cn('flex w-full flex-col items-center', className)}
      style={{ gap: designTokens.spacing.lg }}
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit?.()
      }}
      noValidate
    >
      <ContentGroup variant="registrationDesktop" className="w-full">
        <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
          {desktopBack}
          <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.md }}>
            <h2 style={titleStyle}>{title}</h2>
            <div style={subtitleStyle}>{subtitle}</div>
          </div>
          <div
            className="flex w-full flex-col items-center"
            style={{ gap: designTokens.spacing.md }}
          >
            {codeBlock}
            {button}
          </div>
        </div>
      </ContentGroup>
      <div style={supportStyle}>{supportText}</div>
    </form>
  )
}
